import { Button } from "@/src/components/atom/Button";
import Loader from "@/src/components/atom/Loader";
import Typo from "@/src/components/atom/Typo";
import FormItem from "@/src/components/molecule/FormItem";
import TabItem from "@/src/components/molecule/TabItem";
import SharedEmailLayout from "@/src/components/template/Layout/SharedEmailLayout";
import { TEMPLATE_OPTION_VALUE } from "@/src/constant/emailDialog";
import { sessionStorageKeySet } from "@/src/constant/sessionstorage";
import useAlert from "@/src/hooks/useAlert";
import { useLazyGetBookingDetailQuery } from "@/src/store/apis/bookings/bookingDetail";
import { BookingDetailViewDto } from "@/src/store/apis/bookings/bookingDetail/interface";
import { useLazyGetBookingSharedQuery } from "@/src/store/apis/bookings/bookingShare";
import {
  useLazyGetContractDetailQuery,
  useLazyGetExporterContractSignatureHistoriesQuery,
} from "@/src/store/apis/contracts/contractDetail";
import {
  ContractDetailViewDto,
  ContractSignature,
} from "@/src/store/apis/contracts/contractDetail/interface";
import { ContractDetailShareInfoDto } from "@/src/store/apis/contracts/contractShare/interface";
import colorSet from "@/src/styles/color";
import { aesDecrypt } from "@/src/utils/aesDecrypt";
import { isNull } from "@/src/utils/is";
import { transformURLSearchParamsToObject } from "@/src/utils/transform";
import { useCallback, useEffect, useLayoutEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useLocation, useSearchParams } from "react-router-dom";
import styled from "styled-components";
import CertificationCodeLayout from "./components/CertificationCode";
import BookingItem from "./components/tabs/BookingItem";
import ContractItem from "./components/tabs/ContractItem";
import { SHARED_SEARCH_PARAMS_STRINGS } from "./constants";
import { useLazyGetContractSharedSignatureQuery } from "@/src/store/apis/contracts/contractShare";

type TabType = "CONTRACT" | "BOOKING";

export type BookingDecrypt = {
  buyerId: string;
  bookingId: string;
  bookingShareKey: string;
  contractShareKey: string;
};

const SharedBookingPage = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const alert = useAlert();
  const location = useLocation();
  const sharedBookingEncryptKey = location.search.slice(1);

  // Preview API
  const [
    getPreviewContract,
    { isError: isPreviewContractError, isFetching: isPreviewContractFetching },
  ] = useLazyGetContractDetailQuery();
  const [
    getPreviewBooking,
    { isError: isPreviewBookingError, isFetching: isPreviewBookingFetching },
  ] = useLazyGetBookingDetailQuery();
  const [
    getContractPreviewSignature,
    {
      isError: isContractPreviewSignatureError,
      isFetching: isContractPreviewSignatureFetching,
    },
  ] = useLazyGetExporterContractSignatureHistoriesQuery();

  // API
  const [
    getBookingSharedData,
    { contractData, bookingData, isError, isSharedBookingFetching },
  ] = useLazyGetBookingSharedQuery({
    selectFromResult: ({ currentData, isError, isFetching }) => {
      return {
        isSharedBookingFetching: isFetching,
        isError,
        contractData: currentData?.row.contractDetailShareInfo,
        bookingData: currentData?.row.bookingDetailShareInfo,
      };
    },
  });
  const [
    getContractSharedSignature,
    {
      isError: isContractSignatureError,
      isFetching: isContractSignatureFetching,
    },
  ] = useLazyGetContractSharedSignatureQuery();

  // Query
  // 복호화된 Query String 객체
  const bookingDecryptToObject = transformURLSearchParamsToObject(
    aesDecrypt(
      sharedBookingEncryptKey.includes("?lng=")
        ? sharedBookingEncryptKey.split("?lng=")[0]
        : sharedBookingEncryptKey
    )
  ) as BookingDecrypt;

  const bookingSessionKeysArray = window.sessionStorage.getItem(
    sessionStorageKeySet.SHARED_BOOKING
  )
    ? (JSON.parse(
        window.sessionStorage.getItem(
          sessionStorageKeySet.SHARED_BOOKING
        ) as string
      ) as string[])
    : [];
  const isIncludesBookingSessionKey = bookingSessionKeysArray?.includes(
    sharedBookingEncryptKey
  );

  // 미리보기용 Query
  const isPreview =
    searchParams.get(SHARED_SEARCH_PARAMS_STRINGS.preview) === "true";
  const previewBookingId = Number(
    searchParams.get(SHARED_SEARCH_PARAMS_STRINGS.bookingId)
  );
  const previewContractId = Number(
    searchParams.get(SHARED_SEARCH_PARAMS_STRINGS.contractId)
  );
  const previewTabList = (searchParams
    .get(SHARED_SEARCH_PARAMS_STRINGS.template)
    ?.split(",") as TabType[]) ?? [TEMPLATE_OPTION_VALUE.booking];
  const isPreviewContractTab = previewTabList.includes(
    TEMPLATE_OPTION_VALUE.contract as TabType
  );

  // State
  const [previewContractData, setPreviewContractData] =
    useState<ContractDetailViewDto>();
  const [previewBookingData, setPreviewBookingData] =
    useState<BookingDetailViewDto>();
  const [previewSignature, setPreviewSignature] = useState<ContractSignature[]>(
    []
  );
  const [contractSignatureData, setContractSignatureData] = useState<
    ContractSignature[]
  >([]);
  const [isCodeConfirm, setIsCodeConfirm] = useState(false);
  const [tabList, setTabList] = useState<TabType[]>(
    isPreview ? previewTabList : []
  );
  const [selectTab, setSelectTab] = useState<TabType>(tabList[0]);
  const { control, getValues, handleSubmit, setFocus } = useForm<{
    bookingShareKey: string;
  }>({
    mode: "onBlur",
    defaultValues: {
      bookingShareKey: "",
    },
  });

  const isFetching =
    isSharedBookingFetching ||
    isPreviewContractFetching ||
    isPreviewBookingFetching ||
    isContractPreviewSignatureFetching ||
    isContractSignatureFetching;

  // 공유코드 입력후 공유용 API Fetching
  const handleSubmitClick = async () => {
    const params = {
      contractShareKey: bookingDecryptToObject.contractShareKey,
      bookingShareKey: getValues("bookingShareKey"),
      bookingId: Number(bookingDecryptToObject.bookingId),
    };

    try {
      const res = await getBookingSharedData(params).unwrap();

      if (!isNull(res.row.contractDetailShareInfo)) {
        const signatureRes = await getContractSharedSignature({
          contractId: res?.row.contractDetailShareInfo.id,
          contractShareKey: params.contractShareKey,
        }).unwrap();

        setContractSignatureData(signatureRes.rows || []);
      }

      const jsonArray = JSON.stringify([
        ...bookingSessionKeysArray,
        sharedBookingEncryptKey,
      ]);

      window.sessionStorage.setItem(
        sessionStorageKeySet.SHARED_BOOKING,
        jsonArray
      );

      if (!isNull(res.row.contractDetailShareInfo)) {
        setTabList(["CONTRACT", "BOOKING"]);
        setSelectTab("CONTRACT");
      } else {
        setSelectTab("BOOKING");
      }

      setIsCodeConfirm(true);
    } catch (e: any) {
      console.log("error::", e);
      const message = Array.isArray(e?.data?.message)
        ? e.data.message[0]
        : e.data.message;

      setIsCodeConfirm(false);
      alert.showAlert({ type: "error", message });
    }
  };

  // 마운트 시 query내 복호화된 shareKey가 입력되었을 때 공유용 API
  const fetchWhenMount = useCallback(async () => {
    const params = {
      contractShareKey: bookingDecryptToObject.contractShareKey,
      bookingShareKey: bookingDecryptToObject.bookingShareKey,
      bookingId: Number(bookingDecryptToObject.bookingId),
    };

    try {
      const res = await getBookingSharedData(params).unwrap();

      if (!isNull(res.row.contractDetailShareInfo)) {
        const signatureRes = await getContractSharedSignature({
          contractId: res?.row.contractDetailShareInfo.id,
          contractShareKey: params.contractShareKey,
        }).unwrap();

        setContractSignatureData(signatureRes.rows || []);
        setTabList(["CONTRACT", "BOOKING"]);
        setSelectTab("CONTRACT");
      } else {
        setSelectTab("BOOKING");
      }
    } catch (e: any) {
      const message = Array.isArray(e?.data?.message)
        ? e.data.message[0]
        : e.data.message;

      setIsCodeConfirm(false);
      alert.showAlert({ type: "error", message });
    }
  }, [
    alert,
    bookingDecryptToObject,
    getBookingSharedData,
    getContractSharedSignature,
  ]);

  // 미리보기 형식 상세정보 Fetching
  const getPreviewContractData = async () => {
    try {
      const res = await getPreviewContract({
        id: previewContractId,
      }).unwrap();
      const signResponse = await getContractPreviewSignature({
        id: previewContractId,
      }).unwrap();

      setPreviewContractData(res);
      setPreviewSignature(signResponse.rows || []);
    } catch (e: any) {
      const message = Array.isArray(e?.data?.message)
        ? e.data.message[0]
        : e.data.message;

      alert.showAlert({ type: "error", message });
    }
  };
  const getPreviewBookingData = async () => {
    try {
      const res = await getPreviewBooking({
        id: previewBookingId,
      }).unwrap();

      setPreviewBookingData(res);
    } catch (e: any) {
      const message = Array.isArray(e?.data?.message)
        ? e.data.message[0]
        : e.data.message;

      alert.showAlert({ type: "error", message });
    }
  };

  const renderTabItemTitle = (type: TabType) => {
    switch (type) {
      case "CONTRACT":
        return "common:contract";

      case "BOOKING":
        return "common:booking";
    }
  };

  const renderTabItemContent = (tabType: TabType) => {
    const tabContentData = {
      contractData: isPreview ? previewContractData : contractData,
      signatureData: isPreview ? previewSignature : contractSignatureData,
      isContractError: isPreview
        ? isPreviewContractError || isContractPreviewSignatureError
        : isError || isContractSignatureError,

      bookingData: isPreview ? previewBookingData : bookingData,
      isBookingError: isPreview ? isPreviewBookingError : isError,
    };

    switch (tabType) {
      case "CONTRACT":
        return (
          <ContractItem
            contractData={
              tabContentData.contractData as
                | ContractDetailShareInfoDto
                | ContractDetailViewDto
            }
            signatureData={tabContentData.signatureData}
            isError={tabContentData.isContractError}
            isSignatureDataFetching={
              isPreview
                ? isContractPreviewSignatureFetching
                : isContractSignatureFetching
            }
          />
        );
      case "BOOKING":
        return (
          <BookingItem
            bookingData={tabContentData.bookingData}
            isError={tabContentData.isBookingError}
          />
        );
    }
  };

  const renderSharedEmailContent = () => {
    if (
      isSharedBookingFetching ||
      isPreviewContractFetching ||
      isPreviewBookingFetching ||
      isContractPreviewSignatureFetching ||
      isContractSignatureFetching
    ) {
      return (
        <LoaderContainer>
          <Loader size={60} />
        </LoaderContainer>
      );
    }

    return (
      <>
        {tabList.includes("CONTRACT") && (
          <Tabs role="tablist">
            {tabList.map((item, idx) => {
              return (
                <StyledTabItem
                  key={idx.toString()}
                  tabIndex={item === selectTab ? 0 : -1}
                  data-selected={item === selectTab}
                  tabValue={item}
                  onClick={() => setSelectTab(item)}
                  onFocusItem={(value) => {
                    setSelectTab(value as TabType);
                  }}
                >
                  <Typo
                    typoType="b4m"
                    color={item === selectTab ? "gray2" : "gray6"}
                  >
                    {t(renderTabItemTitle(item))}
                  </Typo>
                </StyledTabItem>
              );
            })}
          </Tabs>
        )}

        {renderTabItemContent(selectTab)}
      </>
    );
  };

  useLayoutEffect(() => {
    if (isPreview) {
      setIsCodeConfirm(true);
      isPreviewContractTab && getPreviewContractData();
      getPreviewBookingData();
    }
    if (isIncludesBookingSessionKey) {
      setIsCodeConfirm(true);
      fetchWhenMount();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setFocus("bookingShareKey");
  }, [setFocus]);

  return (
    <>
      {!isCodeConfirm ? (
        <CertificationCodeLayout
          title={t("common:checkShareBookingInformation")}
        >
          <Form onSubmit={handleSubmit(handleSubmitClick)}>
            <FormItem
              label={t("common:certificationCode")}
              type="text"
              name="bookingShareKey"
              rules={{ required: true }}
              errorsMessage={{
                required: t("error:required"),
              }}
              control={control}
              direction="vertical"
              inputProps={{
                placeholder: t("placeholder:enterCode"),
              }}
            />

            <StyledButton type="submit">{t("common:confirm")}</StyledButton>
          </Form>
        </CertificationCodeLayout>
      ) : (
        <SharedEmailLayout isLoading={isFetching}>
          {renderSharedEmailContent()}
        </SharedEmailLayout>
      )}
    </>
  );
};
export default SharedBookingPage;

const Tabs = styled.nav`
  display: flex;
  gap: 16px;
  margin: 24px 0;
  border-bottom: 1px solid ${colorSet.gray9};
`;

const StyledTabItem = styled(TabItem)`
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 1;
  padding: 8px 16px;
  cursor: pointer;
  background: none;
  border: none;

  &[data-selected="true"] {
    padding: 8px 16px 6px;
    border-bottom: 2px solid ${colorSet.gray2};
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 40px;
`;

const StyledButton = styled(Button)`
  text-align: center;
`;

const LoaderContainer = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;
